import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import type { RespondentType } from './useAdminContext';
import './AdminFilters.css';

interface SchoolInfo {
  name: string;
  type: 'site-based' | 'virtual' | 'staff';
  gradeRange?: string;
  surveyTypes?: string[];
}

export interface FilterState {
  surveyPeriod: string;
  school: string;
  surveyType: string;
  surveyTypes: string[];
  respondentType: RespondentType;
}

interface AdminFiltersProps {
  onFiltersChange: (filters: FilterState) => void;
  showSurveyPeriod?: boolean;
  initialSchool?: string;
  initialSurveyType?: string;
  respondentType?: RespondentType;
}

// Survey type labels for user-friendly display
const SURVEY_TYPE_LABELS: Record<string, string> = {
  'k-2': 'K-2 Grades',
  '3-5': 'Grades 3-5',
  '6-8': 'Grades 6-8',
  'high-school': 'High School',
  'virtual-k8': 'Virtual K-8',
  'virtual-hs': 'Virtual High School',
  'staff-climate': 'Staff Climate',
};

// Survey types per respondent role
const SURVEY_TYPES_BY_ROLE: Record<RespondentType, string[]> = {
  student: ['k-2', '3-5', '6-8', 'high-school', 'virtual-k8', 'virtual-hs'],
  staff: ['staff-climate'],
  parent: [],
};

// All survey types for "All Schools" dropdown (legacy default)
const ALL_SURVEY_TYPES = SURVEY_TYPES_BY_ROLE.student;

const AdminFilters: React.FC<AdminFiltersProps> = ({
  onFiltersChange,
  showSurveyPeriod = true,
  initialSchool = '',
  initialSurveyType = '',
  respondentType = 'student',
}) => {
  const { token } = useAuth();
  const [schools, setSchools] = useState<SchoolInfo[]>([]);
  const [surveyPeriod, setSurveyPeriod] = useState('2026-q1');
  const [school, setSchool] = useState(initialSchool);
  const [surveyType, setSurveyType] = useState(initialSurveyType);

  // Role-aware survey types
  const roleSurveyTypes = SURVEY_TYPES_BY_ROLE[respondentType] || ALL_SURVEY_TYPES;

  // Fetch schools on mount
  useEffect(() => {
    const fetchSchools = async () => {
      if (!token) return;
      try {
        const response = await fetch('/api/admin/schools', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setSchools(data.schools || []);
        }
      } catch (err) {
        console.error('Failed to fetch schools:', err);
      }
    };
    fetchSchools();
  }, [token]);

  // Get survey types for selected school, filtered by role
  const getSchoolSurveyTypes = useCallback((): string[] => {
    if (!school) {
      return roleSurveyTypes;
    }
    const schoolInfo = schools.find((s) => s.name === school);
    const schoolTypes = schoolInfo?.surveyTypes || roleSurveyTypes;
    // Intersect with role types
    return schoolTypes.filter((t) => roleSurveyTypes.includes(t));
  }, [school, schools, roleSurveyTypes]);

  // Check if grade band filter is needed
  const needsGradeBandFilter = useCallback((): boolean => {
    const surveyTypes = getSchoolSurveyTypes();
    return surveyTypes.length > 1;
  }, [getSchoolSurveyTypes]);

  // Reset survey type when respondent type changes
  useEffect(() => {
    setSurveyType('');
  }, [respondentType]);

  // Update filters and notify parent
  useEffect(() => {
    const surveyTypes = getSchoolSurveyTypes();
    const effectiveSurveyType = surveyType && surveyTypes.includes(surveyType)
      ? surveyType
      : surveyTypes[0] || '';

    // Auto-select first survey type if current selection is invalid
    if (surveyType !== effectiveSurveyType) {
      setSurveyType(effectiveSurveyType);
    }

    onFiltersChange({
      surveyPeriod,
      school,
      surveyType: effectiveSurveyType,
      surveyTypes,
      respondentType,
    });
  }, [surveyPeriod, school, surveyType, getSchoolSurveyTypes, onFiltersChange, respondentType]);

  // Handle school change - reset survey type if needed
  const handleSchoolChange = (newSchool: string) => {
    setSchool(newSchool);
    // Reset survey type when school changes
    setSurveyType('');
  };

  const surveyTypes = getSchoolSurveyTypes();
  // Hide grade band filter for staff (only one survey type, no grade bands)
  const showGradeBand = respondentType === 'student' && needsGradeBandFilter();

  return (
    <div className="admin-filters">
      {showSurveyPeriod && (
        <div className="admin-filters__group">
          <label className="admin-filters__label">Survey Period</label>
          <select
            value={surveyPeriod}
            onChange={(e) => setSurveyPeriod(e.target.value)}
            className="admin-filters__select"
          >
            <option value="2026-q1">2026-q1</option>
          </select>
        </div>
      )}

      <div className="admin-filters__group">
        <label className="admin-filters__label">School</label>
        <select
          value={school}
          onChange={(e) => handleSchoolChange(e.target.value)}
          className="admin-filters__select"
        >
          <option value="">All Schools</option>
          {schools.map((s) => (
            <option key={s.name} value={s.name}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {showGradeBand && (
        <div className="admin-filters__group">
          <label className="admin-filters__label">
            {school ? 'Grade Band' : 'Survey Type'}
          </label>
          <select
            value={surveyType}
            onChange={(e) => setSurveyType(e.target.value)}
            className="admin-filters__select"
          >
            {surveyTypes.map((type) => (
              <option key={type} value={type}>
                {SURVEY_TYPE_LABELS[type] || type}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default AdminFilters;
